/**
 * Troubleshooting Category Banner
 *
 * Compact illustration featuring a single lightbulb representing
 * insight and solutions. Designed to work as a standalone icon
 * alongside text content in a merged header layout.
 */

import type { CategoryBannerProps } from './index';

export function TroubleshootingBanner({
  width = 100,
  height = 100,
  className = '',
}: CategoryBannerProps) {
  return (
    <svg
      className={className}
      width={width}
      height={height}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Troubleshooting illustration"
      style={{ maxWidth: '100%', height: 'auto' }}
    >
      <defs>
        {/* Soft glow behind the bulb */}
        <radialGradient id="troubleGlow" cx="50%" cy="45%" r="50%">
          <stop offset="0%" stopColor="#d4ccf0" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#f5f3fb" stopOpacity="0" />
        </radialGradient>
        {/* Gradient for the bulb glass */}
        <linearGradient id="bulbGlass" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f5f3fb" />
          <stop offset="50%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#ede9f9" />
        </linearGradient>
        {/* Gradient for the filament glow */}
        <linearGradient id="filamentGlow" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#9d8de5" />
          <stop offset="100%" stopColor="#8b7bd8" />
        </linearGradient>
      </defs>

      {/* Ambient glow */}
      <circle cx="50" cy="42" r="40" fill="url(#troubleGlow)" />

      {/* Light rays emanating from bulb */}
      <g stroke="#d4ccf0" strokeWidth="2" strokeLinecap="round">
        <line x1="50" y1="8" x2="50" y2="14" />
        <line x1="75" y1="18" x2="71" y2="22" />
        <line x1="25" y1="18" x2="29" y2="22" />
        <line x1="82" y1="42" x2="76" y2="42" />
        <line x1="18" y1="42" x2="24" y2="42" />
      </g>

      {/* Lightbulb glass */}
      <path
        d="M50 18
           C36 18 26 28 26 42
           C26 50 30 56 36 62
           L36 68
           L64 68
           L64 62
           C70 56 74 50 74 42
           C74 28 64 18 50 18Z"
        fill="url(#bulbGlass)"
        stroke="#8b7bd8"
        strokeWidth="2"
      />

      {/* Inner highlight on glass */}
      <path
        d="M42 28
           C36 32 34 40 36 48"
        fill="none"
        stroke="#ffffff"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.7"
      />

      {/* Filament */}
      <path
        d="M42 52 L42 44 L46 48 L50 42 L54 48 L58 44 L58 52"
        fill="none"
        stroke="url(#filamentGlow)"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Bulb base/screw cap */}
      <rect x="38" y="68" width="24" height="6" rx="1" fill="#9d8de5" />
      <rect x="40" y="74" width="20" height="4" rx="1" fill="#8b7bd8" />
      <rect x="42" y="78" width="16" height="4" rx="2" fill="#8b7bd8" />

      {/* Base ridges for screw cap detail */}
      <line x1="38" y1="71" x2="62" y2="71" stroke="#8b7bd8" strokeWidth="1" />
      <line x1="40" y1="76" x2="60" y2="76" stroke="#7a6bc7" strokeWidth="1" />
    </svg>
  );
}
